import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import api from '@/axios';
import {
  GET_DOCUMENT_TYPES_KEY,
  GET_PURCHASE_ORDER_ACTIVITY_KEY,
  GET_PURCHASE_ORDER_DOCUMENTS_KEY,
  GET_PURCHASE_ORDER_KEY,
  UPLOAD_PURCHASE_ORDER_DOCUMENT_KEY,
} from '@/keys';
import {
  PROFORMA_SIGNED_DOCUMENT_TYPE_CODE,
  uploadSignedProformaDocument,
} from '@/services/apis/mutations/purchase-orders/upload-signed-proforma-document';
import {
  PROFORMA_UNSIGNED_DOCUMENT_TYPE_CODE,
  uploadUnsignedProformaDocument,
} from '@/services/apis/mutations/purchase-orders/upload-unsigned-proforma-document';
import type { DocumentType } from '@/types/core/document-type';
import type { UploadPurchaseOrderDocumentParams } from '@/types/core/purchase-order/upload-purchase-order-document-params';

const uploadPurchaseOrderDocumentWithoutTransition = async ({
  purchaseOrderId,
  typeId,
  file,
}: UploadPurchaseOrderDocumentParams): Promise<void> => {
  const formData = new FormData();
  formData.append('typeId', String(typeId));
  formData.append('file', file);

  await api.post(`/purchase-orders/${purchaseOrderId}/documents`, formData, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData) {
          headers['Content-Type'] = undefined;
        }
        return data;
      },
    ],
  });
};

export const useUploadPurchaseOrderDocument = (): UseMutationResult<
  void,
  Error,
  UploadPurchaseOrderDocumentParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: UPLOAD_PURCHASE_ORDER_DOCUMENT_KEY,
    mutationFn: (params) => {
      const documentTypes = queryClient.getQueryData<DocumentType[]>(
        GET_DOCUMENT_TYPES_KEY,
      );
      const selectedTypeCode =
        documentTypes?.find((dt) => dt.id === params.typeId)?.code ?? null;

      switch (selectedTypeCode) {
        case PROFORMA_UNSIGNED_DOCUMENT_TYPE_CODE:
          return uploadUnsignedProformaDocument(queryClient, params);
        case PROFORMA_SIGNED_DOCUMENT_TYPE_CODE:
          return uploadSignedProformaDocument(queryClient, params);
        default:
          return uploadPurchaseOrderDocumentWithoutTransition(params);
      }
    },
    onSuccess: async (_data, { purchaseOrderId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...GET_PURCHASE_ORDER_KEY, purchaseOrderId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...GET_PURCHASE_ORDER_DOCUMENTS_KEY, purchaseOrderId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...GET_PURCHASE_ORDER_ACTIVITY_KEY, purchaseOrderId],
        }),
      ]);
    },
  });
};
